<template>
  <v-container fluid class="">
    <v-dialog :value="contentScaleRejected !== null" max-width="420" persistent data-cy="platings-scale-warning">
      <v-card>
        <v-card-title>Scale out of range</v-card-title>
        <v-card-text>
          "{{ contentScaleRejected }}" cannot be used as the scale. The scale is a fraction of actual size
          between {{ printScaleRange.min }} and {{ printScaleRange.max }}: 1 prints at actual size, and 0.6 shrinks
          everything to 60% so more fits on a page. It has been set back to 1.
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text color="primary" data-cy="platings-scale-warning-ok" @click="contentScaleRejected = null">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div class="d-print-none">
      <v-toolbar flat class="d-print-none">
        <ProductionSelector
          :dates="dates" :production="production"
          v-on:update:dates="dates=$event"
          v-on:update:production="production=$event"
        />
        <v-spacer/>
        <v-select
          label="Streams/Diets"
          v-model="selectedDiets"
          item-text="name"
          item-value="id"
          :items="activeDiets"
          single-line
          hide-details
          multiple
        />
        <v-checkbox
          single-line
          hide-details
          label="Show Restrictions"
          v-model="showRestriction"
        />
        <v-checkbox
          class="ml-4"
          single-line
          hide-details
          label="Kitchen Wall Sort"
          v-model="kitchenWallSort"
          @change="applyKitchenWallSortDefaults"
        />
        <v-btn icon @click="gridPreview = !gridPreview" :color="gridPreview ? 'primary' : ''"
               title="Grid Preview (3×2)">
          <v-icon>mdi-view-grid</v-icon>
        </v-btn>
        <DebouncedTextField
          v-model.number="contentScale"
          :debounce="500"
          label="Scale"
          hint="Scale"
          persistent-hint
          type="number"
          step="0.05"
          :min="printScaleRange.min"
          :max="printScaleRange.max"
          single-line
          style="max-width: 80px"
        />
      </v-toolbar>
      <!--    {{  {loaded, loadedCount, 'filteredMeals.length': filteredMeals.length, isLoaded} }}-->
      <v-alert v-if="!loaded" type="info" class="d-print-none">
      <span v-if="loadingOrders">
        Please wait while the orders are loaded.
      </span>
        <span v-if="!loadingOrders">
        Please wait while the meals are loaded {{ loadedCount }} of {{ platingPages.length }}
      </span>
      </v-alert>
      <v-alert v-if="loadingOrders === false && loaded && platingPages.length === 0" type="info">
        No {{ showRestriction ? 'allergy' : 'regular' }} plating pages for this production
        {{ selectedDiets.length > 0 ? 'and diets' : '' }}.
      </v-alert>
      <v-alert v-if="kitchenWallSort && !gridPreview" type="warning">
        Kitchen Wall Sort Print Settings:
        <ul>
          <li>Portrait Mode</li>
          <li>6 pages per sheet</li>
          <li>Scale to 85%</li>
        </ul>
      </v-alert>
    </div>
    <div :class="{'grid-preview': gridPreview}">
      <div
        class="plating-page-wrapper"
        v-for="({id,date,restrictions, ordersForMeal}) of platingPages"
        v-bind:key="`${id}-${restrictions?'a':''}`"
      >
        <v-container
          fluid class="plating-page page-break full-page-portrait ma-0 pa-0 "
          :style="pageStyle"
        >
          <!--
           v-if="restrictions?isLoaded[id]:true"
           NOTE: this delays loading rendering the allergy version until the non-allergy version is loaded
           because rendering both at the same time causes a vue infinite update loop.  :'(

               -->
          <Plating
            class=""
            :date="date"
            :dates="dates"
            :production="production"
            :orders-in="ordersForMeal"
            :meal-id="id"
            hideControls
            read-only
            :restrictions="restrictions"
            skip-url-sync
            v-on:is-loading="$set(isLoaded,id,!$event)"
          />
          <!--      -->
        </v-container>
      </div>
    </div>
  </v-container>
</template>


<script>
import moment from "moment/moment";
import {getProductionDays} from "@/store/utils";
import api from "@/api";
import Plating from "@/components/Plating.vue";
import urlState from "@/router/urlState";
import {mapActions, mapGetters, mapState} from "vuex";
import ProductionSelector from "@/components/ProductionSelector.vue";
import DebouncedTextField from "@/components/DebouncedTextField.vue";
import diff from "microdiff";
import {PRINT_SCALE_RANGE, printScaleFromUrl, rejectedPrintScale} from "@/utils/print-scale";

export default {
  name: "Platings",
  components: {ProductionSelector, Plating, DebouncedTextField},
  mixins: [urlState],
  async mounted() {
    this.syncToUrl({
      param: 'dates', urlParam: 'dates', initFromRoute: true,
      parseCallback: (v) => Array.isArray(v) ? v : [v]
    });
    this.syncToUrl({
      param: 'production', urlParam: 'production', initFromRoute: true,
    });
    this.syncToUrl({
      param: 'selectedDiets', urlParam: 'diets', initFromRoute: true,
      parseCallback: (v) => (Array.isArray(v) ? v : [v]).map(i => Number(i))
    });
    const boolToUrl = {parseCallback: (v) => v === 'true', transformCallback: (v) => v ? 'true' : null};
    this.syncToUrl({param: 'showRestriction', urlParam: 'restrictions', initFromRoute: true, ...boolToUrl});
    this.syncToUrl({param: 'kitchenWallSort', urlParam: 'wallSort', initFromRoute: true, ...boolToUrl});
    this.syncToUrl({param: 'gridPreview', urlParam: 'grid', initFromRoute: true, ...boolToUrl});
    this.syncToUrl({
      param: 'contentScale', urlParam: 'scale', initFromRoute: true,
      parseCallback: printScaleFromUrl,
      transformCallback: (v) => v === 1 ? null : v
    });
    this.$nextTick(() => this.fetchData());
    await this.fetchDiets();
  },
  methods: {
    ...mapActions(['fetchDiets', 'fetchMealIngredients', 'fetchMeal']),
    closeDatePicker() {
      this.showDatePicker = false;
      this.$nextTick(() => this.fetchData());
    },
    // only cascade when the user toggles the checkbox, not when restoring state from the url
    applyKitchenWallSortDefaults(enabled) {
      this.gridPreview = enabled;
      if (enabled) {
        this.showRestriction = false;
      }
    },
    sortMeals() {
      const meals = this.meals;
      if (this.kitchenWallSort) {
        const key = m => `${m.diet}-${m.date}-${m.tod}`;
        const mealSort = (a, b) => key(a).localeCompare(key(b));
        meals.sort(mealSort);
        this.meals = meals;
      } else {
        const key = m => (m.plating_rack || '').padStart(8, '0') + `-${m.date}-${m.diet}-${m.tod}`;
        const mealSort = (a, b) => key(a).localeCompare(key(b));
        meals.sort(mealSort);
        this.meals = meals;
      }
    },
    async fetchData() {
      this.meals = [];
      this.orders = [];
      this.hasAllergyDetected = {};
      this.isLoaded = {};
      if (this.dateFrom && this.dateTo) {
        this.loadingOrders = true;
        console.log('fetching meal ids in range', this.dateFrom, this.dateTo);
        const {data} = await api.get('v2/meal/ids', {params: {from: this.dateFrom, to: this.dateTo}});
        console.log('got ', data);

        const {dates, production} = this;
        if (!dates || dates.length === 0) {
          console.log('no dates, no fetch');
          this.loading = false;
          return;
        }
        if (!production) {
          console.log('no production, no fetch');
          this.loading = false;
          return;
        }

        if (this.currentData && diff(this.currentData, {dates, production}).length === 0) {
          console.log('already loaded', this.currentData);
          return
        } else {
          console.log('fetching prep', {dates, production});
          this.currentData = {dates, production};
        }

        const params = {
          from: this.dateFrom,
          to: this.dateTo,
          productions: [this.production],
          // properties: ['quantity', 'allergyDetected', 'allergies', 'meal_id', 'stream', 'tod', 'meal_size'],
        };
        console.log('fetching ', params);
        const orders = await api
          .get('v2/order/search', {params})
          .then(({data}) => data);
        this.ordersFetched = orders;

        console.log('orders', orders);
        this.orders = orders;
        this.hasAllergyDetected = {};
        this.hasRegularMeals = {};
        orders.forEach(o => {
          if (o.allergyDetected) {
            this.hasAllergyDetected[o.meal_id] = true
          } else {
            this.hasRegularMeals[o.meal_id] = true
          }
        });
        const orderedMealIds = new Set(orders.map(o => o.meal_id));
        console.log('orderedMealIds', orderedMealIds);

        // let meals = data.filter(({id}) => orderedMealIds.has(id));
        // meals = await Promise.all(meals.map(({id}) => this.fetchMeal(id)));
        this.meals = await Promise.all(
          [...orderedMealIds]
            .filter(id => !!id)
            .map(id => this.fetchMeal(id)));
        this.sortMeals();
        // await Promise.all(meals.map(({id}) => this.fetchMealIngredients({id})));
        this.activeDiets = data.map(d => this.diets[d.diet]);
        this.activeDiets.sort((a, b) => a.id - b.id);
        console.log('filtered meals', this.meals);
        this.$nextTick(() => this.loadingOrders = false);
      } else {
        console.log('no dates selected');
      }
    },
  },
  data() {
    return {
      showDatePicker: null,
      datePickerDate: null,
      dates: [],
      meals: [],
      showRestriction: false,
      kitchenWallSort: false,
      isLoaded: {},
      selectedDiets: [],
      activeDiets: [],
      orders: [],
      hasAllergyDetected: {},
      hasRegularMeals: {},
      loadingOrders: null,
      production: null,
      currentData: null,
      gridPreview: false,
      contentScale: 1,
      printScaleRange: PRINT_SCALE_RANGE,
      // what was typed into the scale field when it could not be used, while the dialog explains
      contentScaleRejected: null,
    }
  },
  watch: {
    contentScale(value) {
      const rejected = rejectedPrintScale(value);
      if (rejected !== null) {
        this.contentScaleRejected = rejected;
        this.contentScale = 1;
      }
    },
    dates: 'fetchData',
    production: 'fetchData',
    kitchenWallSort: 'sortMeals',
    showDatePicker(v) {
      if (v) {
        if (this.isAdmin) {
          this.datePickerDate = this.dates;
        } else {
          this.datePickerDate = this.dates[0];
        }
      } else {
        if (this.isAdmin) {
          this.dates = this.datePickerDate;
        } else {
          this.dates = getProductionDays(this.datePickerDate)
        }
      }
    },
  },
  computed: {
    ...mapState(['diets']),
    ...mapGetters(['getDietName']),
    loadedCount() {
      return Object.values(this.isLoaded).filter(s => s).length;
    },
    loaded() {
      // only the rendered pages report loaded: a meal with allergy-only orders has no regular page
      return !this.loadingOrders && this.loadedCount >= this.platingPages.length;
    },
    datesFormatted() {
      const format = 'dddd MMMM D';
      if (!this.dateFrom) {
        return '';
      } else if (this.dateFrom === this.dateTo) {
        return `${moment(this.dateFrom).format(format)}`;
      } else {
        return `${moment(this.dateFrom).format(format)} - ${moment(this.dateTo).format(format)}`
      }
    },
    dateFrom() {
      return [...this.dates].sort()[0];
    },
    dateTo() {
      return [...this.dates].sort().reverse()[0];
    },
    /**
     * Scale shrinks the content of every plating sheet (zoom lays it out wider, so more fits on
     * a page). In the grid preview it also divides by 3 so three sheets fit across.
     */
    pageStyle() {
      const scale = this.contentScale > 0 ? this.contentScale : 1;
      if (this.gridPreview) {
        return {zoom: scale / 3, width: `${300 / scale}%`};
      }
      return scale === 1 ? {} : {zoom: scale};
    },
    filteredMeals() {
      if (this.selectedDiets && this.selectedDiets.length > 0) {
        return this.meals.filter(m => this.selectedDiets.includes(m.diet));
      } else {
        return this.meals;
      }
    },
    platingPages() {
      let platingPages = [];
      if (!this.showRestriction) {
        this.filteredMeals.forEach(m => {
          if (this.hasRegularMeals[m.id]) {
            platingPages.push(m);
          }
        })
      } else {
        this.filteredMeals.forEach(m => {
          if (this.hasAllergyDetected[m.id]) {
            platingPages.push({...m, restrictions: true});
          }
        })
      }


      // handle CC meals
      const orders = this.orders;
      const ccOrders = orders.filter(o => o.order_type === 'chefs_choice');
      const ccOrderMap = {};
      ccOrders.forEach(o => {
        ccOrderMap[o.meal_id] = ccOrderMap[o.meal_id] || [];
        ccOrderMap[o.meal_id].push(o);
      })
      console.log('ccOrderMap', ccOrderMap);
      platingPages = platingPages.map(m => {
        if (ccOrderMap[m.id]) {
          const date = ccOrderMap[m.id][0].date;
          return {
            ...m,
            ordersForMeal: ccOrderMap[m.id],
            date
          }
        } else {
          return m;
        }
      })

      return platingPages; //.slice(0, 2);
    }
  }
}
</script>

<style scoped>
.page-break {
  page-break-after: always;
}

.grid-preview {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
}

.grid-preview .plating-page-wrapper {
  width: 33.33%;
  overflow: hidden;
  padding: 16px;
}

.grid-preview .plating-page {
  /* zoom and width are set via inline style, driven by the contentScale input (see data.contentScale).
   * zoom = contentScale / 3  (fits 3 columns; contentScale adjusts how much content fills each cell)
   * width = 300% / contentScale  (expands to natural full width before zoom shrinks it back)
   */
  page-break-after: auto;
}

.grid-preview .plating-page-wrapper:nth-child(6n) {
  break-after: page;
}
</style>